import { useNavigate, useParams } from 'react-router-dom';
import { Box, Breadcrumbs, CircularProgress, FormControlLabel, Switch, Typography } from '@mui/material';
import dayjs from '../../../../config/dayjs';

import { Container, MainContainer } from '../../../../components';
import { Menu } from '../Menu/Menu';

import { getAlertSummary, getCenterSummary } from "../../../../services/dashboardService";

import ARROW from '../../../../assets/images/breadcrumb-arrow.svg';
import ARROW_OPEN from '../../../../assets/images/breadcrumb-arrow-open.svg';
import CANDIDATES_ICON from '../../../../assets/images/candidates.svg';
import EXAM_ICON from '../../../../assets/images/exam.svg';
import EXAM_HALL_ICON from '../../../../assets/images/examHall.svg';

import { useContext, useEffect, useRef, useState } from 'react';
import { ProjectContext } from '../../../../providers/projectProvider';
import { AuthContext } from '../../../../providers/auth';
import { buildCenterRings, buildCenters } from './centerSummary';
import { CenterReport } from './CenterReport';
import { CenterDetailsModal } from './CenterDetailsModal';
import { SampleValue } from './dashboardPlaceholders';
import { AlertBell } from './AlertBell';
import { DashboardFilters } from './DashboardFilters';
import { useDashboardFilters } from './useDashboardFilters';

const AUTO_REFRESH_MS = 60 * 1000;

const kpiCardSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1.5,
    bgcolor: 'background.base',
    py: 2,
    px: 2.5,
    border: '1px solid',
    borderColor: 'grey.300',
    borderRadius: 1.5,
};

const panelSx = {
    width: '100%',
    minWidth: 0,
    bgcolor: 'background.base',
    p: 1.5,
    borderRadius: 2,
};

const KpiCard = ({ title, value, icon, isSample }) => (
    <Box sx={kpiCardSx}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.08em', color: 'grey.600' }}>{title}</Typography>
            <Typography sx={{ fontSize: 32, fontWeight: 600, lineHeight: 1.1 }}>{isSample ? <SampleValue>{value}</SampleValue> : value}</Typography>
        </Box>
        <Box sx={{
            display: 'grid',
            height: 38,
            width: 38,
            flexShrink: 0,
            placeContent: 'center',
            bgcolor: '#3F41D1',
            borderRadius: '50%',
        }}>
            <img style={{ height: 20, width: 20 }} src={icon} alt='' />
        </Box>
    </Box>
);

// Centers done / total. Red track while pending, same ring style as the old summary cards.
const CenterRing = ({ title, done, total, isSample }) => {
    const complete = total > 0 && done === total;
    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            gap: 1,
            p: 1.5,
            backgroundColor: 'grey.100',
            border: '1px solid',
            borderColor: 'grey.400',
            borderRadius: 2,
        }}>
            <Typography sx={{ fontSize: 10, fontWeight: 600, letterSpacing: '.08em', color: 'grey.600', lineHeight: 1.2, textAlign: 'center', textTransform: 'uppercase', minHeight: 32, display: 'flex', alignItems: 'center' }}>{isSample ? <SampleValue>{title}</SampleValue> : title}</Typography>
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <CircularProgress
                    size={84}
                    thickness={6}
                    variant="determinate"
                    value={100}
                    sx={{ color: complete || !total ? 'grey.300' : 'error.light', opacity: complete || !total ? 1 : 0.45 }}
                />
                <CircularProgress color='success'
                    size={84}
                    thickness={6}
                    variant="determinate"
                    value={total ? (done * 100) / total : 0}
                    sx={{ position: 'absolute', left: 0 }}
                />
                <Box sx={{
                    display: 'grid',
                    placeContent: 'center',
                    position: 'absolute',
                    inset: 0,
                }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{`${done}/${total}`}</Typography>
                </Box>
            </Box>
        </Box>
    );
}


export const Dashboard = () => {

    const params = useParams();
    const navigate = useNavigate();
    const projectName = params?.projectName;

    const { projectDetails, projectLoading } = useContext(ProjectContext);
    const { user } = useContext(AuthContext);

    const [dashboardStats, setDashboardStats] = useState({
        exams: [],
        totalCandidates: 0,
        loggedInCandidatesCount: 0,
        loggedInCandidatesPercentage: 0,
    });
    // Saved per project and user, so filters come back after visiting another page (until refresh / token expiry)
    const filters = useDashboardFilters({ saveKey: `dashboard-${projectName}-${user?.id}` });
    const isVenueBased = projectDetails?.examMode === 'VENUE_BASED';

    const [centers, setCenters] = useState([]);
    const [selectedCenter, setSelectedCenter] = useState(null);
    // Security alerts per venue (synced venue deviceLog); the list of one venue is shown on the Alert Details page
    const [alertVenues, setAlertVenues] = useState([]);
    const [alertTabRequest, setAlertTabRequest] = useState(0);
    const reportRef = useRef(null);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [statsError, setStatsError] = useState(false);
    // Filters applied by the last "Go"; auto refresh re-uses them so unsaved dropdown changes are not applied silently
    const appliedBodyRef = useRef(filters.savedAppliedBody);

    // Separate from the center counts so an alert failure never blocks the rest of the dashboard
    const fetchAlertSummary = async (body) => {
        try {
            const res = await getAlertSummary(body);
            if (res.code === 'OK') {
                // Venue code, city and state have no source on central yet, so they are left empty and shown as "-"
                setAlertVenues(res.data.venues);
            }
        } catch (error) {
            console.log('There is an error while fetching alert summary', error);
        }
    }

    const fetchDashboardStats = async (appliedBody) => {
        try {
            const body = appliedBody ?? filters.getFilterBody();
            appliedBodyRef.current = body;
            filters.saveAppliedBody(body);
            // Counts only (one row per center); candidate lists are loaded on click in CenterDetailsModal
            const res = await getCenterSummary(body);

            if (res.code === 'OK') {
                const examTotals = res.data.examTotals ?? [];
                const totalCandidates = examTotals.reduce((sum, total) => sum + total.scheduled, 0);
                const loggedInCandidatesCount = examTotals.reduce((sum, total) => sum + total.present, 0);
                const loggedInCandidatesPercentage = totalCandidates ? parseFloat(((loggedInCandidatesCount * 100) / totalCandidates).toFixed(2)) : 0;

                setDashboardStats({
                    exams: res.data.exams.map((exam) => {
                        const total = examTotals.find((t) => t.projectMasterId === exam.id);
                        exam.candidatesCount = total?.scheduled ?? 0;
                        exam.loggedInCandidatesCount = total?.present ?? 0;
                        exam.loggedInCandidatesPercentage = exam.candidatesCount ? parseFloat(((exam.loggedInCandidatesCount * 100) / exam.candidatesCount).toFixed(2)) : 0;
                        return exam;
                    }),
                    totalCandidates: totalCandidates,
                    loggedInCandidatesCount: loggedInCandidatesCount,
                    loggedInCandidatesPercentage: loggedInCandidatesPercentage,
                })
                setCenters(buildCenters(res.data));
                setLastUpdated(dayjs());
                setStatsError(false);
                if (isVenueBased) {
                    fetchAlertSummary(body);
                }
            } else {
                setStatsError(true);
            }
        } catch (error) {
            console.log('There is an error while fetching dashboard statistics', error);
            setStatsError(true);
        }
    }

    // Project details can arrive after the first dashboard load, which then skipped the alerts;
    // load them once the project is known to be venue-based, with the filters already applied
    useEffect(() => {
        if (isVenueBased && appliedBodyRef.current) {
            fetchAlertSummary(appliedBodyRef.current);
        }
    }, [isVenueBased]);

    // Keep the latest fetch for the interval without restarting it on every render
    const fetchRef = useRef(fetchDashboardStats);
    fetchRef.current = fetchDashboardStats;

    useEffect(() => {
        if (!autoRefresh) {
            return;
        }
        const interval = setInterval(() => {
            // Skip while the browser tab is hidden to avoid load during the live exam
            if (document.visibilityState === 'visible' && appliedBodyRef.current) {
                fetchRef.current(appliedBodyRef.current);
            }
        }, AUTO_REFRESH_MS);
        return () => clearInterval(interval);
    }, [autoRefresh]);

    const centerRings = buildCenterRings(centers);

    // Back on the dashboard: reload with the filters applied last time
    useEffect(() => {
        fetchDashboardStats(filters.savedAppliedBody);
    }, []);

    const onGoToExamSummary = (exam) => {
        navigate(`/${projectName}/exam-summary`, {
            state: {
                projectMasterId: exam.id,
            }
        });
    }

    // Alerts of one venue open on their own page, with the date the dashboard is showing
    const onGoToAlertDetails = (venue) => {
        navigate(`/${projectName}/alert-details`, {
            state: {
                venueMasterId: venue.venueMasterId,
                examDate: appliedBodyRef.current?.examDate,
            }
        });
    }

    // Venue / non-venue layout depends on examMode, so wait for the project instead of showing the wrong one first
    if (projectLoading && !projectDetails) {
        return (
            <MainContainer>
                <Container>
                    <Box sx={{ display: 'grid', placeItems: 'center', p: 6 }}>
                        <CircularProgress size={32} />
                    </Box>
                </Container>
            </MainContainer>
        );
    }

    return (
        <MainContainer>
            <Menu>
                <Breadcrumbs aria-label="breadcrumb" separator='' sx={{ gap: 12 }}>
                    <Typography color="text.primary" sx={{ fontWeight: 400, textTransform: 'capitalize' }}>{projectDetails?.projectName}</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW} alt=''></img>
                    </Box>

                    <Typography color="text.primary" sx={{ fontWeight: 500 }}>Dashboard</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW_OPEN} alt='' ></img>
                    </Box>
                </Breadcrumbs>
            </Menu>
            <Container>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <h1 className='title' style={{ fontSize: 22, fontWeight: 600 }}>Dashboard</h1>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 'auto', mb: 3 }}>
                        {lastUpdated && (
                            <Typography sx={{ fontSize: 13, color: statsError ? 'error.main' : 'grey.600' }}>
                                {statsError ? 'Update failed · showing data from ' : 'Last updated '}{lastUpdated.format('HH:mm:ss')}
                            </Typography>
                        )}
                        <FormControlLabel
                            control={<Switch checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />}
                            label='Auto refresh'
                        />
                        {isVenueBased && (
                            <AlertBell
                                alertVenues={alertVenues}
                                lastUpdated={lastUpdated}
                                onViewVenue={onGoToAlertDetails}
                                onOpenAlertTab={() => {
                                    setAlertTabRequest((request) => request + 1);
                                    reportRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }}
                            />
                        )}
                    </Box>
                </Box>

                <DashboardFilters filters={filters} onGo={() => fetchDashboardStats()} />

                {statsError && !lastUpdated && (
                    <Typography sx={{ fontSize: 13, color: 'error.main', mb: 2 }}>Dashboard data is unavailable right now. It will retry automatically.</Typography>
                )}

                <Box sx={{ ...panelSx, mb: 2 }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 600, mb: 1 }}>Over all summary</Typography>
                    {/* No city on central yet (next phase), so the City card shows "-" */}
                    <Box className='dashboard-cards-wrapper' sx={{ mb: '0 !important' }}>
                        <KpiCard title='Exam' value={dashboardStats?.exams?.length ?? 0} icon={EXAM_ICON} />
                        <KpiCard title='Candidate' value={dashboardStats?.totalCandidates ?? 0} icon={CANDIDATES_ICON} />
                        {isVenueBased && <KpiCard title='City' value='-' icon={EXAM_HALL_ICON} isSample />}
                        {isVenueBased && <KpiCard title='Center' value={centers.length} icon={EXAM_HALL_ICON} />}
                    </Box>
                </Box>

                <Box sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'stretch',
                    gap: 2,
                    mb: 2,
                    // One exam card: Exam-wise and Summary by Centers side by side.
                    // More than one: Summary by Centers moves to the next line (full width).
                    '& > .exam-wise-panel': { flex: '0 1 auto', maxWidth: '100%', width: 'auto' },
                    '& > .centers-panel': { flex: dashboardStats.exams.length > 1 ? '1 1 100%' : '1 1 640px' },
                }}>
                    <Box className='exam-wise-panel' sx={panelSx}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
                            <Typography sx={{ fontSize: 15, fontWeight: 600 }}>Exam-wise Summary</Typography>
                            <Typography sx={{ fontSize: 12, color: 'grey.600' }}>Present vs total</Typography>
                        </Box>
                        {/* Cards grow sideways (horizontal scroll) when more exams are shown */}
                        <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 0.5 }}>
                            {dashboardStats.exams.map((exam, index) => <Box key={index} sx={{
                                flex: '0 0 auto',
                                width: 232,
                                p: 2,
                                bgcolor: 'background.base',
                                borderRadius: 1.5,
                                border: '1px solid',
                                borderColor: 'grey.300',
                                cursor: 'pointer',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.25,
                            }} onClick={() => onGoToExamSummary(exam)}>
                                <Typography noWrap title={`${exam?.['drive.driveName']} / ${exam?.['course.courseName']} / ${exam?.['batch.batchName']}`} sx={{ fontSize: 12, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: 'primary.main' }}>
                                    {exam?.['course.courseName']}
                                </Typography>
                                <Typography sx={{ fontSize: 32, fontWeight: 600, lineHeight: 1.1 }}>
                                    {exam.loggedInCandidatesCount} / {exam.candidatesCount}
                                </Typography>
                                <Box sx={{ height: 8, bgcolor: 'grey.300', borderRadius: 1, overflow: 'hidden' }}>
                                    <Box sx={{ height: '100%', width: `${exam.loggedInCandidatesPercentage}%`, bgcolor: 'primary.main' }} />
                                </Box>
                                <Typography sx={{ fontSize: 13, color: 'grey.600' }}>{`${exam.loggedInCandidatesPercentage.toFixed(2)}% present`}</Typography>
                            </Box>)}
                        </Box>
                        {!dashboardStats.exams.length && (
                            <Typography sx={{ fontSize: 13, color: 'grey.500', textAlign: 'center', p: 2 }}>No records available</Typography>
                        )}
                    </Box>

                    {isVenueBased ? (
                        <Box className='centers-panel' sx={panelSx}>
                            <Typography sx={{ fontSize: 15, fontWeight: 600, mb: 1 }}>Summary by Centers</Typography>
                            <Typography sx={{ fontSize: 12, color: 'grey.600', mb: 1 }}>Centers done / total</Typography>
                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))', lg: 'repeat(7, minmax(0, 1fr))' },
                                gap: 1,
                            }}>
                                {centerRings.map((ring) => <CenterRing key={ring.title} {...ring} />)}
                            </Box>
                        </Box>
                    ) : (
                        <Box className='centers-panel' sx={panelSx}>
                            <Typography sx={{ fontSize: 15, fontWeight: 600, mb: 1 }}>Summary</Typography>
                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(6, minmax(0, 1fr))' },
                                gap: 1,
                            }}>
                                <CenterRing title='Candidate Logged In' done={dashboardStats.loggedInCandidatesCount} total={dashboardStats.totalCandidates} />
                            </Box>
                        </Box>
                    )}
                </Box>

                {isVenueBased && (
                    <Box ref={reportRef}>
                        <CenterReport centers={centers} onCenterClick={setSelectedCenter} alertVenues={alertVenues} onAlertClick={onGoToAlertDetails} alertTabRequest={alertTabRequest} />
                    </Box>
                )}
                <CenterDetailsModal center={selectedCenter} onClose={() => setSelectedCenter(null)} />
            </Container>
        </MainContainer>
    );
}
